import React from 'react';
import Header from '../component/Header';
import Footer from '../component/Footer';

const PrivacyPolicy = () => {
  const companyDetails = {
    name: 'Dershelas Ltd',
    address: 'Stadiou 15, 2867 Nicosia, Cyprus',
    email: 'support@learnaihub.net',
  };

  const privacyData = [
    {
      id: "1",
      title: "1. Consent to Privacy Policy",
      content: "By accessing and using our Site and Services, you acknowledge that you have read and agree to this Privacy Policy."
    },
    {
      id: "2",
      title: "2. Information Collection and Personally Identifiable Information",
      content: "We collect the following personal data:",
      list: [
        "Registration data: email address, password, first name, last name, phone number, and payment details.",
        "Usage data: information about your browser, network, or device.",
        "Marketing data: details related to communication and other interaction campaigns."
      ],
      box: {
        title: "Legal bases",
        list: [
          "Contract performance (access to services)",
          "Legitimate interests (fraud prevention, network improvements)",
          "Compliance with legal obligations (e.g., accounting, tax)"
        ]
      },
      subSection: {
        title: "Your rights",
        content: "You may request accessing, correcting, or deleting your data, restrict processing, or object to processing (especially marketing). We will honor such requests unless an overriding legal obligation applies.",
        content2: "We ensure processing to GDPR compliant data processors, such as hosting providers and payment processors. We may also be legally required to disclose information to authorities (e.g., police authorities)."
      }
    },
    {
      id: "3",
      title: "3. Information Use",
      list: [
        "Processing payment transactions",
        "Customizing services, ads, and recommendations",
        "Contacting you and responding to requests",
        "Conducting research and improving services",
        "Preventing fraud and ensuring security"
      ]
    },
    {
      id: "4",
      title: "4. Information Sharing and Disclosure",
      content: "We will not disclose your personally identifiable information except:",
      list: [
        "When required by law",
        "To our service providers (e.g., hosting, payment processing)",
        "In connection with corporate transactions (e.g., merger, sale)",
        "With your explicit consent or at your direction"
      ],
      footerContent: "We may share aggregate, anonymous information with partners, advertisers, or content distributors."
    },
    {
      id: "5",
      title: "5. Changing or Deleting Your Information",
      content: "You may review, update, correct, or delete your personal information by contacting us. Note that we may retain your data for legally required recordkeeping purposes."
    },
    {
      id: "6",
      title: "6. Use of Cookies and Similar Technologies",
      content: "The merchant uses cookies, web beacons, device fingerprinting, and similar technologies for functionality, analytics, personalization, and advertising.",
      grid: {
        title: "Types of cookies used include:",
        items: [
          "Strictly necessary cookies",
          "Performance cookies",
          "Functional cookies",
          "Targeting/advertising cookies"
        ]
      },
      footerContent: "Cookies help improve navigation, remember preferences, and display relevant ads. You can manage cookies in your browser settings. Blocking cookies may affect site functionality.",
      footerContent2: "We may use Google Analytics and Facebook Pixel for tracking and marketing."
    },
    {
      id: "7",
      title: "7. Behavioral Advertising and Online Privacy",
      content: "We may use cookies and online identifiers for behavioral advertising. We follow industry standards like Network Advertising Initiative.",
      footerContent: "You can manage cookies via browser settings, but some site functions may not work if cookies are disabled."
    },
    {
      id: "8",
      title: "8. Changes and Updates",
      content: "We may periodically update this Privacy Policy. We will notify you by posting the modified terms on site. Your continued use implies agreement."
    },
    {
      id: "9",
      title: "9. Contact",
      content: `Questions, comments, and requests regarding this Privacy Policy are welcomed and should be addressed to ${companyDetails.name}, ${companyDetails.address}, or by email at ${companyDetails.email}.`
    }
  ];

  return (
    <>
      <Header />
      
      {/* Page Background */}
      <div className="bg-[#f8f9fa] min-h-screen font-sans pt-24 pb-16">
        
        {/* Main Content Container */}
        <main className="max-w-4xl mx-auto px-6">
          
          {/* White Card */}
          <div className="bg-white rounded-[2rem] shadow-sm border border-zinc-100 p-8 md:p-14 mb-16">
            
            {/* Header Section */}
            <div className="mb-10">
              <h1 className="text-4xl md:text-5xl font-black text-[#042b1f] tracking-tight mb-4">
                Privacy Policy
              </h1>
 
              <div className="h-1.5 w-12 bg-amber-400 rounded-full mb-8"></div>
              
              <div className="space-y-4 text-zinc-600 text-sm md:text-base leading-relaxed font-medium">
                <p className="font-bold text-[#042b1f] uppercase tracking-wide text-xs">Last updated: March 18, 2026</p>
                <p>
                  Controller details: {companyDetails.name}, {companyDetails.address}, {companyDetails.email}.
                </p>
              </div>
            </div>

            {/* Terms Content Iteration */}
            <div className="space-y-10">
              {privacyData.map((section) => (
                <div key={section.id} className="space-y-4">
                  <h2 className="text-xl font-bold text-[#042b1f]">
                    {section.title}
                  </h2>
                  
                  {section.content && (
                    <p className="text-zinc-600 text-sm md:text-base leading-relaxed font-medium">
                      {section.content}
                    </p>
                  )}
                  
                  {/* Standard List */}
                  {section.list && (
                    <ul className="list-disc list-inside text-zinc-600 text-sm md:text-base leading-relaxed font-medium space-y-2 pl-2">
                      {section.list.map((item, index) => (
                        <li key={index} className="pl-2 relative">
                    
                            <span className="ml-4">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Legal Bases Box (Specific to Section 2) */}
                  {section.box && (
                    <div className="bg-zinc-50 border border-zinc-100 rounded-2xl p-6 my-6">
                      <h3 className="font-bold text-[#042b1f] mb-3 text-sm md:text-base">{section.box.title}</h3>
                      <ul className="space-y-2">
                        {section.box.list.map((item, index) => (
                          <li key={index} className="text-zinc-600 text-sm md:text-base font-medium flex items-start gap-3">
          
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Sub-section (Specific to Section 2) */}
                  {section.subSection && (
                    <div className="space-y-3 pt-2">
                      <h3 className="font-bold text-[#042b1f] text-sm md:text-base">{section.subSection.title}</h3>
                      <p className="text-zinc-600 text-sm md:text-base leading-relaxed font-medium">
                        {section.subSection.content}
                      </p>
                      {section.subSection.content2 && (
                        <p className="text-zinc-600 text-sm md:text-base leading-relaxed font-medium">
                          {section.subSection.content2}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Grid Layout (Specific to Section 6 Cookies) */}
                  {section.grid && (
                    <div className="my-6">
                      <h3 className="font-bold text-[#042b1f] mb-4 text-sm md:text-base">{section.grid.title}</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {section.grid.items.map((item, index) => (
                          <div key={index} className="bg-zinc-50 border border-zinc-100 rounded-xl p-4 text-zinc-600 font-medium text-sm md:text-base">
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Footer Content */}
                  {section.footerContent && (
                    <p className="text-zinc-600 text-sm md:text-base leading-relaxed font-medium pt-2">
                      {section.footerContent}
                    </p>
                  )}
                  {section.footerContent2 && (
                    <p className="text-zinc-600 text-sm md:text-base leading-relaxed font-medium">
                      {section.footerContent2}
                    </p>
                  )}
                </div>
              ))}
            </div>

          </div>


        </main>
      </div>

      <Footer />
    </>
  );
};

export default PrivacyPolicy;